import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Sparkles, ChevronRight, Eye, EyeOff } from 'lucide-react';
import { FaGoogle, FaMicrosoft } from 'react-icons/fa';
import { useAuth } from './AuthContext';
import { useAppContext } from '../components/appprovider';
import { signInWithGoogle, signInWithMicrosoft } from '../services/firebaseAuth';
import yakiImage from '../main.png';

/* ============================================================
   Auth screen — styled-components port of the reference Login.
   Two columns: form (left) + brand panel (right). No global CSS
   (App.css removed); all values are the reference tokens, scoped
   to these components so nothing leaks into the rest of the app.
   ============================================================ */

const FONT = '"Hanken Grotesk", system-ui, sans-serif';
const ACCENT = 'oklch(0.55 0.21 274)';
const ACCENT_STRONG = 'oklch(0.48 0.22 274)';
const BG = 'oklch(0.975 0.004 260)';
const SURFACE = 'oklch(1 0 0)';
const SURFACE2 = 'oklch(0.985 0.004 260)';
const BORDER = 'oklch(0.915 0.006 260)';
const TEXT = 'oklch(0.26 0.02 268)';
const TEXT_MUTED = 'oklch(0.52 0.018 268)';
const TEXT_SUBTLE = 'oklch(0.64 0.014 268)';
const RAIL_BG = 'oklch(0.21 0.012 250)';
const BTN_BG = 'oklch(0.22 0.008 270)';
const BTN_BG_HOVER = 'oklch(0.3 0.01 270)';
const DANGER = 'oklch(0.6 0.21 25)';
const SUCCESS = 'oklch(0.62 0.15 158)';

// Imported (not referenced from /public) so the bundler emits a content-hashed
// filename — this cache-busts the image on every deploy, so a stale CDN/browser
// copy can't keep showing an old logo after the asset changes.
const LOGO_SRC = yakiImage;

const Page = styled.div`
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  background: ${BG};
  font-family: ${FONT};
  color: ${TEXT};
  -webkit-font-smoothing: antialiased;

  @media (max-width: 860px) { grid-template-columns: 1fr; }
`;

const FormCol = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 48px clamp(32px, 7vw, 110px);
  overflow-y: auto;
`;

const BrandCol = styled.div`
  position: relative;
  overflow: hidden;
  background: ${RAIL_BG};
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 48px clamp(28px, 5vw, 72px);
  color: #fff;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(120% 80% at 100% 0%, color-mix(in oklch, ${ACCENT} 50%, transparent), transparent 60%);
  }

  .mascot {
    position: absolute;
    right: -24px;
    bottom: -16px;
    width: 460px;
    max-width: 62%;
    height: auto;
    z-index: 1;
    pointer-events: none;
    user-select: none;
    opacity: 0.32;
  }

  @media (max-width: 1100px) {
    .mascot { width: 360px; }
  }
  @media (max-width: 860px) { display: none; }
`;

const BrandInner = styled.div`
  position: relative;
  z-index: 2;

  .pill { display: inline-flex; align-items: center; gap: 8px; padding: 6px 13px; border-radius: 999px; background: rgba(255,255,255,.12); font-size: 12.5px; font-weight: 700; margin-bottom: 24px; }
  h2 { font-family: ${FONT}; font-size: 30px; font-weight: 750; line-height: 1.18; letter-spacing: -.02em; margin: 0 0 16px; max-width: 460px; }
  p { margin: 0; font-size: 15px; color: rgba(255,255,255,.72); max-width: 440px; line-height: 1.6; }
  .stats { display: flex; gap: 30px; margin-top: 40px; }
  .stats b { font-family: ${FONT}; font-weight: 760; font-size: 26px; display: block; font-variant-numeric: tabular-nums; }
  .stats span { font-size: 12.5px; color: rgba(255,255,255,.6); }
`;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 40px;

  .logo {
    width: 44px; height: 44px; border-radius: 11px; flex: none;
    display: grid; place-items: center;
    background: #fff;
    overflow: hidden;
    box-sizing: border-box;
    padding: 5px;
    box-shadow: 0 4px 14px -4px color-mix(in oklch, ${ACCENT} 60%, transparent);
  }
  .logo img { width: 100%; height: 100%; object-fit: contain; display: block; }
  .name { font-family: ${FONT}; font-weight: 750; font-size: 19px; letter-spacing: -.01em; }
`;

const Title = styled.h1`
  font-family: ${FONT};
  font-size: 30px;
  font-weight: 760;
  letter-spacing: -.02em;
  margin: 0 0 6px;
`;

const Sub = styled.p`
  margin: 0 0 28px;
  font-size: 15px;
  color: ${TEXT_MUTED};
`;

const Form = styled.form`
  max-width: 420px;
  width: 100%;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const Field = styled.div`
  margin-bottom: 16px;

  > label { display: block; font-size: 11.5px; font-weight: 650; margin-bottom: 6px; color: ${TEXT}; }
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  height: 40px;
  padding: 0 13px;
  border-radius: 12px;
  border: 1px solid ${BORDER};
  background: ${SURFACE2};
  color: ${TEXT};
  font-family: ${FONT};
  font-size: 13.5px;
  outline: none;
  transition: .15s;

  &::placeholder { color: ${TEXT_SUBTLE}; }
  &:focus {
    border-color: ${ACCENT};
    background: ${SURFACE};
    box-shadow: 0 0 0 3px color-mix(in oklch, ${ACCENT} 12%, ${SURFACE});
  }
`;

const PasswordWrap = styled.div`
  position: relative;

  input { padding-right: 42px; }

  button {
    position: absolute;
    top: 0;
    right: 0;
    height: 40px;
    width: 40px;
    display: grid;
    place-items: center;
    border: none;
    background: none;
    padding: 0;
    cursor: pointer;
    color: ${TEXT_SUBTLE};
    transition: color .15s;
  }
  button:hover { color: ${TEXT}; }
  svg { width: 17px; height: 17px; }
`;

const RowBetween = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const ForgotLink = styled(Link)`
  font-size: 11.5px;
  color: ${ACCENT_STRONG};
  font-weight: 650;
  text-decoration: none;
  cursor: pointer;
`;

const PolicyLink = styled.a`
  color: ${ACCENT_STRONG};
  font-weight: 650;
  text-decoration: underline;
  cursor: pointer;
`;

const CheckRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 9px;
  font-size: 11.5px;
  font-weight: 600;
  margin: 4px 0 22px;
  cursor: pointer;
  line-height: 1.4;
  color: ${TEXT};

  input { width: 16px; height: 16px; margin-top: 1px; accent-color: ${ACCENT}; flex: none; }
`;

const PrimaryBtn = styled.button`
  width: 100%;
  height: 46px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid transparent;
  border-radius: 12px;
  background: ${BTN_BG};
  color: #fff;
  font-family: ${FONT};
  font-size: 13.5px;
  font-weight: 650;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 1px 2px rgba(17,20,38,.18), inset 0 1px 0 rgba(255,255,255,.12);
  transition: background .15s, box-shadow .15s, transform .08s;

  &:hover:not(:disabled) { background: ${BTN_BG_HOVER}; }
  &:active { transform: translateY(1px); }
  &:disabled { opacity: .7; cursor: default; }
  svg { width: 16px; height: 16px; }
`;

const OAuthRow = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 420px) { flex-direction: column; }
`;

const OAuthBtn = styled.button`
  flex: 1;
  min-width: 0;
  height: 46px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  border: 1px solid ${BORDER};
  border-radius: 12px;
  background: ${SURFACE};
  color: ${TEXT};
  font-family: ${FONT};
  font-size: 13.5px;
  font-weight: 650;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(17,20,38,.06);
  transition: .15s;

  &:hover { background: ${SURFACE2}; border-color: ${TEXT_SUBTLE}; }
  svg { width: 18px; height: 18px; flex: none; }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 20px 0;
  color: ${TEXT_SUBTLE};
  font-size: 11.5px;

  &::before, &::after { content: ""; flex: 1; height: 1px; background: ${BORDER}; }
`;

const ToggleP = styled.p`
  margin-top: 30px;
  font-size: 11.5px;
  color: ${TEXT_MUTED};

  button {
    border: none; background: none; padding: 0;
    color: ${ACCENT_STRONG}; font-weight: 650; cursor: pointer;
    font-family: ${FONT}; font-size: 11.5px;
  }
`;

const Message = styled.div`
  margin: -6px 0 16px;
  padding: 9px 12px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  ${({ $success }) => $success
    ? `color: ${SUCCESS}; background: color-mix(in oklch, ${SUCCESS} 12%, ${SURFACE});`
    : `color: ${DANGER}; background: color-mix(in oklch, ${DANGER} 12%, ${SURFACE});`}
`;

export default function Login({ setIsLoggedIn }) {
  const apiUrl = process.env.REACT_APP_API_URL;
  const apiUrl1 = process.env.REACT_APP_API_URL1;
  const { setUserId, setPackageDetails } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const isEnterpriseLogin = 1;
  const [data, setData] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    password: '',
    userType: '4',
  });
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Falls back to an icon mark if the logo asset fails to load.
  const [logoOk, setLogoOk] = useState(true);

  const handleChangeEmail = (e) => {
    const emailValue = e.target.value;
    setEmail(emailValue);
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue);
    if (!isValidEmail) {
      setError('Please enter a valid email address.');
    } else {
      setError('');
    }
  };

  const handleChangePassword = (e) => {
    setPassword(e.target.value);
  };

  const validatePassword = (password) => {
    const minLength = password.length >= 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    return { minLength, hasUppercase, hasLowercase, hasNumber, hasSpecialChar };
  };

  const handleChangeSignUp = ({ currentTarget: input }) => {
    const { name, value } = input;
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    if (name === 'email' && !isValidEmail) {
      setError('Please enter a valid email address.');
    } else if (name === 'password') {
      const validation = validatePassword(value);
      if (
        !validation.minLength ||
        !validation.hasUppercase ||
        !validation.hasLowercase ||
        !validation.hasNumber ||
        !validation.hasSpecialChar
      ) {
        setError(
          'Password must be at least 8 characters long, and include an uppercase letter, a lowercase letter, a number, and a special character.'
        );
      } else {
        setError('');
      }
    } else {
      setError('');
    }
    setData({ ...data, [name]: value });
  };

  const { signIn } = useAuth();

  // Shared post-login handling for both password sign-in and SSO — persists the
  // session and navigates into the portal. `res` is the parsed JSON body from
  // /api/user/login or /api/user/google-login (identical shape).
  const applyLoginSuccess = async (res) => {
    const userId = res.data.user.user_id;
    const userType = res.data.user.user_type;
    const packages = location.state;

    const email1 = res.data.user.email;
    const token = res.data.token;
    const firstName = res.data.user.first_name;
    const lastName = res.data.user.last_name;
    localStorage.setItem('stripeAccountId', res.data.user.stripe_account_id);
    if (lastName === '') {
      localStorage.setItem('username', firstName);
    } else {
      localStorage.setItem('username', firstName + ' ' + lastName);
    }
    localStorage.setItem('email', email1);
    localStorage.setItem('isLoggedIn', true);
    localStorage.setItem('userId', userId);
    localStorage.setItem('token', token);
    localStorage.setItem('userType', userType);

    if (packages) {
      localStorage.setItem('packageDetails', packages.package.planId);
      localStorage.setItem('price', packages.package.price);
      localStorage.setItem('name', packages.package.name);
    }

    if (userType === 2) {
      localStorage.setItem('userId1', userId);
    } else {
      localStorage.setItem('userId1', userId);
    }

    setUserId(userId);
    setPackageDetails(packages);

    await signIn();

    // First-login onboarding: show the "How would you like to start?" slides
    // only when last_login is empty (first login); skip them afterwards.
    if (!res.data.user.last_login) {
      localStorage.removeItem('onboardingComplete');
    } else {
      localStorage.setItem('onboardingComplete', '1');
    }

    navigate('/Portal/Instructor', { state: { userId: userId } });
  };

  const handleSubmitSignin = async (e) => {
    e.preventDefault();
    try {
      if (!email || !password) {
        setError('Email and password are required.');
        return;
      }
      setIsLoading(true);

      const body = {
        email: email,
        password: password,
        isEnterpriseLogin: isEnterpriseLogin,
      };

      const response = await fetch(apiUrl1 + '/api/user/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
          'Access-Control-Allow-Origin': '*',
          "X-Api-Version": 'v2'
        },
        body: JSON.stringify(body),
        insecure: true,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      await new Promise((resolve) => setTimeout(resolve, 1000));
      const res = await response.json();
      await applyLoginSuccess(res);
    } catch (error) {
      console.error('Error:', error);
      setError('Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const getBasicAuthHeader = () => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const encodedCredentials = btoa(`${username}:${password}`);
    return `Basic ${encodedCredentials}`;
  };

  const handleSubmitSignUp = async (e) => {
    e.preventDefault();
    const sqlInjectionPattern = /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|ALTER|CREATE|TRUNCATE)\b|;|'|--)/i;
    if (sqlInjectionPattern.test(data.firstName) ||
      sqlInjectionPattern.test(data.lastName) ||
      sqlInjectionPattern.test(data.companyName) ||
      sqlInjectionPattern.test(data.email) ||
      sqlInjectionPattern.test(data.password)) {
      setError("Invalid input detected.");
      return;
    }

    if (!data.firstName || !data.lastName || !data.companyName || !data.email || !data.password) {
      setError("All fields are required.");
      return;
    }

    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email);
    if (!isValidEmail) {
      setError("Please enter a valid email address.");
      return;
    }

    const validation = validatePassword(data.password);
    if (!validation.minLength || !validation.hasUppercase || !validation.hasLowercase ||
      !validation.hasNumber || !validation.hasSpecialChar) {
      setError(
        "Password must be at least 8 characters, include uppercase, lowercase, number, and special character."
      );
      return;
    }

    try {
      setIsLoading(true);
      const url = apiUrl + '/api/signup';
      const { data: res } = await axios.post(url, data, {
        headers: {
          'Content-Type': "application/json",
          "Accept": "*/*",
          "Access-Control-Allow-Origin": "*",
          'Authorization': getBasicAuthHeader(),
        },
      });
      if (res === 'User created') {
        setMsg('Verify your email to sign in');
        setTimeout(() => {
          setMsg('');
          setIsSignUp(false);
        }, 7000);
      }
    } catch (error) {
      if (error.response && error.response.status === 409) {
        setError('User already exists. Please sign in instead.');
      } else if (
        error.response &&
        error.response.status >= 400 &&
        error.response.status <= 500
      ) {
        setError(error.response.data.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleRedirection = async () => {
      const userId = localStorage.getItem('userId');
      if (userId) {
        const packages = location.state;
        if (packages) {
          localStorage.setItem('packageDetails', packages.package.planId);
          localStorage.setItem('price', packages.package.price);
          localStorage.setItem('name', packages.package.name);
        }
        setUserId(userId);
        navigate('/Portal/Instructor', { state: { userId } });
      }
    };
    handleRedirection();
  }, [navigate, location, setUserId]);

  const toggleMode = () => {
    setIsSignUp((v) => !v);
    setError('');
    setMsg('');
  };

  // Google / Microsoft SSO via Firebase. Both providers return a Firebase ID
  // token that the backend verifies (isFirebaseLogin: true), then we run the
  // same post-login handling as password sign-in. Works from both the sign-in
  // and sign-up views (the backend find-or-creates the instructor account).
  const handleSsoLogin = async (provider) => {
    setError('');
    setMsg('');
    try {
      setIsLoading(true);
      const idToken = provider === 'google'
        ? await signInWithGoogle()
        : await signInWithMicrosoft();

      const response = await fetch(apiUrl1 + '/api/user/google-login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
          'Access-Control-Allow-Origin': '*',
          'X-Api-Version': 'v2',
        },
        body: JSON.stringify({
          idToken,
          isEnterpriseLogin: 1,
          isFirebaseLogin: true,
          device_type: 'web',
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const res = await response.json();
      await applyLoginSuccess(res);
    } catch (err) {
      // A user closing/cancelling the popup is not a real error — stay silent.
      const code = err && err.code;
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        return;
      }
      console.error('SSO login error:', err);
      const label = provider === 'google' ? 'Google' : 'Microsoft';
      setError(`${label} sign-in failed. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Page>
      <FormCol>
        <LogoRow>
          <div className="logo">
            {logoOk ? (
              <img src={LOGO_SRC} alt="StudyAK" onError={() => setLogoOk(false)} />
            ) : (
              <Sparkles size={22} color={ACCENT} />
            )}
          </div>
          <div className="name">StudyAK</div>
        </LogoRow>

        <Title>{isSignUp ? 'Create your account' : 'Welcome back'}</Title>
        <Sub>{isSignUp ? 'Set up your instructor workspace in a minute.' : 'Sign in to your instructor workspace.'}</Sub>

        {isSignUp ? (
          <Form onSubmit={handleSubmitSignUp}>
            <FormGrid>
              <Field>
                <label>First name</label>
                <Input name="firstName" value={data.firstName} onChange={handleChangeSignUp} placeholder="Maya" />
              </Field>
              <Field>
                <label>Last name</label>
                <Input name="lastName" value={data.lastName} onChange={handleChangeSignUp} placeholder="Reardon" />
              </Field>
            </FormGrid>
            <Field>
              <label>Organisation</label>
              <Input name="companyName" value={data.companyName} onChange={handleChangeSignUp} placeholder="Riverside Health Academy" />
            </Field>
            <Field>
              <label>Work email</label>
              <Input name="email" type="email" value={data.email} onChange={handleChangeSignUp} placeholder="you@org.edu" />
            </Field>
            <Field>
              <label>Password</label>
              <PasswordWrap>
                <Input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={data.password}
                  onChange={handleChangeSignUp}
                  placeholder="At least 8 characters"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff /> : <Eye />}
                </button>
              </PasswordWrap>
            </Field>
            <CheckRow>
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
              <span>
                I agree to the{' '}
                <PolicyLink
                  href="https://studyak.ai/terms-of-use/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  Terms
                </PolicyLink>
                {' '}&amp;{' '}
                <PolicyLink
                  href="https://studyak.ai/privacy-policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  Privacy Policy
                </PolicyLink>
              </span>
            </CheckRow>
            {error && <Message>{error}</Message>}
            {msg && <Message $success>{msg}</Message>}
            <PrimaryBtn type="submit" disabled={isLoading || !agreed}>
              {isLoading ? 'Creating account…' : (<>Create account <ChevronRight /></>)}
            </PrimaryBtn>
            <Divider>or</Divider>
            <OAuthRow>
              <OAuthBtn type="button" onClick={() => handleSsoLogin('google')} disabled={isLoading}>
                <FaGoogle style={{ color: '#EA4335' }} />Google
              </OAuthBtn>
              <OAuthBtn type="button" onClick={() => handleSsoLogin('microsoft')} disabled={isLoading}>
                <FaMicrosoft style={{ color: '#00A4EF' }} />Microsoft
              </OAuthBtn>
            </OAuthRow>
            <ToggleP>
              Already have an account? <button type="button" onClick={toggleMode}>Sign in</button>
            </ToggleP>
          </Form>
        ) : (
          <Form onSubmit={handleSubmitSignin}>
            <Field>
              <label>Work email</label>
              <Input type="email" value={email} onChange={handleChangeEmail} placeholder="you@org.edu" required />
            </Field>
            <Field>
              <RowBetween>
                <label style={{ marginBottom: 0 }}>Password</label>
                <ForgotLink to="/resetpassword">Forgot?</ForgotLink>
              </RowBetween>
              <PasswordWrap style={{ marginTop: 7 }}>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={handleChangePassword}
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff /> : <Eye />}
                </button>
              </PasswordWrap>
            </Field>
            <CheckRow>
              <input type="checkbox" defaultChecked />
              <span>Keep me signed in</span>
            </CheckRow>
            {error && <Message>{error}</Message>}
            {msg && <Message $success>{msg}</Message>}
            <PrimaryBtn type="submit" disabled={isLoading}>
              {isLoading ? 'Signing in…' : (<>Sign in <ChevronRight /></>)}
            </PrimaryBtn>
            <Divider>or</Divider>
            <OAuthRow>
              <OAuthBtn type="button" onClick={() => handleSsoLogin('google')} disabled={isLoading}>
                <FaGoogle style={{ color: '#EA4335' }} />Google
              </OAuthBtn>
              <OAuthBtn type="button" onClick={() => handleSsoLogin('microsoft')} disabled={isLoading}>
                <FaMicrosoft style={{ color: '#00A4EF' }} />Microsoft
              </OAuthBtn>
            </OAuthRow>
            <ToggleP>
              New to StudyAK? <button type="button" onClick={toggleMode}>Create an account</button>
            </ToggleP>
          </Form>
        )}
      </FormCol>

      <BrandCol>
        <BrandInner>
          <div className="pill"><Sparkles size={15} />AI-assisted assessments</div>
          <h2>
            {isSignUp
              ? 'Start building assessments your learners actually finish.'
              : 'Build, assign and grade assessments — without the busywork.'}
          </h2>
          <p>
            Generate question banks from your course PDFs, track every learner&apos;s progress,
            and stay audit-ready across your whole organisation.
          </p>
          <div className="stats">
            {[['274', 'active learners'], ['6', 'courses'], ['91%', 'pass rate']].map(([n, l]) => (
              <div key={l}><b className="tnum">{n}</b><span>{l}</span></div>
            ))}
          </div>
        </BrandInner>
        {logoOk && <img className="mascot" src={LOGO_SRC} alt="" aria-hidden="true" onError={() => setLogoOk(false)} />}
      </BrandCol>
    </Page>
  );
}
